import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DemoClassPanel from '../../components/education/DemoClassPanel';
import api from '../../services/api';
import { shareLocalAllyLink } from '../../services/native-mobile.service';
import {
  dashboardDataService,
  normalizeEntityId,
  resolveSelectionId,
  slotToDate,
  StudentProfile,
  TeacherSelectionApi,
  toDateKey,
} from '../../services/dashboard-data.service';
import { Session } from '../../types/session';

type DashboardSession = Session & {
  selectionId: string;
  sessionDate: string;
  rating?: number;
  review?: string;
  meetingLink?: string;
  meetingCode?: string;
  subjectId?: string;
};

interface DashboardStat {
  name: string;
  value: string;
  change?: string;
  changeType: 'positive' | 'negative' | 'neutral';
}

const durationByMode = (mode: 'ONLINE' | 'OFFLINE' | 'HYBRID') => {
  if (mode === 'OFFLINE') return 90;
  if (mode === 'HYBRID') return 75;
  return 60;
};

const resolveReferralLink = (referralCode: string, rawReferralLink?: string) => {
  const normalizedCode = String(referralCode || '').trim();
  const fallbackLink = `${window.location.origin}/register?ref=${encodeURIComponent(normalizedCode)}`;
  const candidate = String(rawReferralLink || '').trim();

  if (!candidate) return fallbackLink;

  try {
    const parsed = new URL(candidate);
    const fallback = new URL(fallbackLink);
    parsed.protocol = fallback.protocol;
    parsed.host = fallback.host;
    return parsed.toString();
  } catch {
    return fallbackLink;
  }
};

const StudentDashboard: React.FC = () => {
  const { user } = useAuth();
  const [referralCode, setReferralCode] = useState<string>(String((user as any)?.referralCode || '').trim());
  const [referralLink, setReferralLink] = useState<string>('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [studentSelections, setStudentSelections] = useState<TeacherSelectionApi[]>([]);
  const [studentProfile, setStudentProfile] = useState<StudentProfile>({});
  const [subjectLabelMap, setSubjectLabelMap] = useState<Record<string, string>>({});
  const [selectedSession, setSelectedSession] = useState<DashboardSession | null>(null);
  const [attendanceInput, setAttendanceInput] = useState('');
  const [attendanceStatus, setAttendanceStatus] = useState<{ ok: boolean; message: string } | null>(
    null,
  );
  const [showScanner, setShowScanner] = useState(false);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [rescheduleSessionId, setRescheduleSessionId] = useState<string | null>(null);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleDateTime, setRescheduleDateTime] = useState('');
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);

  const isTodayKey = (key: string) => key === toDateKey(new Date());

  const resolveUser = useCallback(() => {
    const direct = user as any;
    if (direct?.id) return direct;
    if (direct?.user?.id) return direct.user;
    return null;
  }, [user]);

  const loadStudentSelections = useCallback(async (currentUserId: string, force = false) => {
    const selections = await dashboardDataService.getStudentSelections(force);
    const filteredSelections = (Array.isArray(selections) ? selections : []).filter(
      (selection) => normalizeEntityId(selection.studentUserId) === String(currentUserId),
    );
    setStudentSelections(filteredSelections);
  }, []);

  useEffect(() => {
    const loadUpcomingSessions = async () => {
      const currentUser = resolveUser();
      setStudentSelections([]);

      if (!currentUser?.id) {
        return;
      }
      try {
        await loadStudentSelections(currentUser.id);
      } catch (error) {
        console.error('Error loading selection-based sessions:', error);
        setStudentSelections([]);
      }
    };

    void loadUpcomingSessions();
  }, [loadStudentSelections, resolveUser]);

  useEffect(() => {
    const loadReferralSummary = async () => {
      try {
        const data = await dashboardDataService.getReferralSummary();
        const nextCode = String(data?.referralCode || '').trim();
        const nextLink = resolveReferralLink(nextCode, String(data?.referralLink || '').trim());
        setReferralCode(nextCode);
        setReferralLink(nextLink);
      } catch (error) {
        console.error('Failed to load referral summary:', error);
      }
    };

    void loadReferralSummary();
  }, []);

  useEffect(() => {
    const loadStudentProfile = async () => {
      try {
        const data = await dashboardDataService.getStudentProfile();
        setStudentProfile(data || {});
      } catch (error) {
        console.error('Error loading student profile:', error);
        setStudentProfile({});
      }
    };
    void loadStudentProfile();
  }, []);

  useEffect(() => {
    setAttendanceInput('');
    setAttendanceStatus(null);
    setShowScanner(false);
    setScannerError(null);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, [selectedSession]);

  useEffect(() => {
    const loadSubjectLabels = async () => {
      try {
        const nextMap = await dashboardDataService.getSubjectLabelMap();
        setSubjectLabelMap(nextMap);
      } catch (error) {
        console.error('Failed to load subject labels:', error);
        setSubjectLabelMap({});
      }
    };
    void loadSubjectLabels();
  }, []);

  const resolveSubjectLabel = React.useCallback(
    (subject?: string, subjectId?: string) => {
      const normalizedSubjectId = String(subjectId || '').trim();
      const normalizedSubject = String(subject || '').trim();
      return (
        subjectLabelMap[normalizedSubjectId] ||
        subjectLabelMap[normalizedSubject] ||
        normalizedSubject ||
        'General'
      );
    },
    [subjectLabelMap],
  );

  const { upcomingSessions, completedSessions } = useMemo(() => {
    const currentUser = resolveUser();
    const currentUserId = String(currentUser?.id || '');
    if (!currentUserId) {
      return { upcomingSessions: [] as DashboardSession[], completedSessions: [] as DashboardSession[] };
    }

    const now = new Date();
    const upcomingMapped: DashboardSession[] = [];
    const completedMapped: DashboardSession[] = [];

    studentSelections.forEach((selection) => {
      const resolvedId = resolveSelectionId(selection);
      if (!resolvedId) return;

      const basePayload = {
        teacherId: selection.teacherId,
        teacherName: selection.teacherName,
        studentId: currentUserId,
        studentName: '',
        subject: selection.subject,
        subjectId: selection.subjectId,
        meetingLink: selection.meetingLink,
        meetingCode: selection.meetingCode,
        classMode: selection.classMode,
        frequency: selection.frequency,
        duration: durationByMode(selection.classMode),
        selectionId: resolvedId,
        rating: selection.studentRating,
        review: selection.studentReview,
      };

      if (selection.frequency === 'DAILY') {
        const statusMap = selection.sessionDailyStatus || {};
        const rescheduledDateTimes = selection.rescheduledDateTimes || {};
        const selectionStart = selection.startDate ? new Date(selection.startDate) : null;
        const selectionEnd = selection.endDate ? new Date(selection.endDate) : null;
        if (selectionStart && !Number.isNaN(selectionStart.getTime())) {
          selectionStart.setHours(0, 0, 0, 0);
        }
        if (selectionEnd && !Number.isNaN(selectionEnd.getTime())) {
          selectionEnd.setHours(23, 59, 59, 999);
        }
        for (let offset = -7; offset <= 6; offset += 1) {
          const day = new Date(now);
          day.setHours(0, 0, 0, 0);
          day.setDate(day.getDate() + offset);
          if (selectionStart && day.getTime() < selectionStart.getTime()) continue;
          if (selectionEnd && day.getTime() > selectionEnd.getTime()) continue;
          const dayKey = toDateKey(day);
          const scheduledAt = rescheduledDateTimes[dayKey] || slotToDate(day.toISOString(), selection.preferredTimeSlot);
          const status = statusMap[dayKey] === 'COMPLETED' ? 'COMPLETED' : 'SCHEDULED';
          const row: DashboardSession = {
            id: `${resolvedId}:${dayKey}`,
            ...basePayload,
            sessionDate: dayKey,
            scheduledAt,
            status,
          };

          if (status === 'COMPLETED') {
            completedMapped.push(row);
          } else if (offset >= 0) {
            upcomingMapped.push(row);
          }
        }
        return;
      }

      const scheduledAt = slotToDate(selection.selectedAt, selection.preferredTimeSlot);
      const baseDayKey = toDateKey(new Date(scheduledAt));
      const scheduledAtResolved = (selection.rescheduledDateTimes || {})[baseDayKey] || scheduledAt;
      const dayKey = toDateKey(new Date(scheduledAtResolved));
      const status = selection.sessionStatus === 'COMPLETED' ? 'COMPLETED' : 'SCHEDULED';
      const row: DashboardSession = {
        id: resolvedId,
        ...basePayload,
        sessionDate: dayKey,
        scheduledAt: scheduledAtResolved,
        status,
      };

      if (status === 'COMPLETED') {
        completedMapped.push(row);
      } else if (new Date(scheduledAtResolved).getTime() >= Date.now()) {
        upcomingMapped.push(row);
      }
    });

    upcomingMapped.sort((left, right) => new Date(left.scheduledAt).getTime() - new Date(right.scheduledAt).getTime());
    completedMapped.sort((left, right) => new Date(right.scheduledAt).getTime() - new Date(left.scheduledAt).getTime());

    return {
      upcomingSessions: upcomingMapped,
      completedSessions: completedMapped,
    };
  }, [resolveUser, studentSelections]);

  const stats: DashboardStat[] = useMemo(
    () => [
      { name: 'Active Sessions', value: String(upcomingSessions.length), changeType: 'neutral' as const },
      { name: 'Completed Sessions', value: String(completedSessions.length), changeType: 'neutral' as const },
      { name: 'Upcoming Tests', value: '0', changeType: 'neutral' as const },
      { name: 'Average Grade', value: '-', changeType: 'neutral' as const },
    ],
    [completedSessions.length, upcomingSessions.length],
  );

  const handleVerifyAttendance = async () => {
    if (!selectedSession) return;
    const raw = attendanceInput.trim();
    if (!raw) {
      setAttendanceStatus({ ok: false, message: 'Please scan QR or enter meeting ID.' });
      return;
    }

    try {
      const response = await api.post('/teachers/selections/validate-meeting', {
        meetingInput: raw,
        selectionId: selectedSession.selectionId,
      });

      if (!response.data.valid) {
        setAttendanceStatus({
          ok: false,
          message: response.data.message,
        });
        return;
      }

      await api.patch(`/teachers/selections/${selectedSession.selectionId}/status`, {
        sessionDate: selectedSession.sessionDate,
        status: 'COMPLETED',
      });

      const currentUser = resolveUser();
      if (currentUser?.id) {
        dashboardDataService.invalidateStudentSelections();
        await loadStudentSelections(currentUser.id, true);
      }

      setSelectedSession((prev) =>
        prev
          ? {
              ...prev,
              status: 'COMPLETED',
            }
          : prev,
      );
      setAttendanceStatus({
        ok: true,
        message: 'Attendance verified and session marked as completed.',
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Validation failed. Please try again.';
      setAttendanceStatus({ ok: false, message: errorMessage });
    }
  };

  const handleStartScanner = async () => {
    setScannerError(null);
    if (!('BarcodeDetector' in window)) {
      setScannerError('QR scanning not supported in this browser. Please enter the code manually.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setShowScanner(true);
      const detector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
      const scan = async () => {
        if (!videoRef.current || !showScanner) return;
        const bitmap = await createImageBitmap(videoRef.current);
        const results = await detector.detect(bitmap);
        if (results && results[0]) {
          const rawValue = results[0].rawValue || '';
          setAttendanceInput(rawValue);
          setShowScanner(false);
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
          }
          handleVerifyAttendance();
          return;
        }
        requestAnimationFrame(scan);
      };
      requestAnimationFrame(scan);
    } catch (error) {
      console.error('Scanner error', error);
      setScannerError('Could not access camera. Please allow camera permission or enter code manually.');
    }
  };

  const isStudentProfileIncomplete = (profile: StudentProfile) => {
    const hasCoreFields =
      Boolean(String(profile.dateOfBirth || '').trim()) &&
      Boolean(String(profile.gender || '').trim()) &&
      Boolean(String(profile.currentLocation || '').trim()) &&
      Boolean(String(profile.address || '').trim());
    const hasClassSubjectCombination =
      Array.isArray(profile.classSubjectCombinations) &&
      profile.classSubjectCombinations.length > 0 &&
      profile.classSubjectCombinations.every(
        (item) =>
          Boolean(String(item.className || '').trim()) &&
          Array.isArray(item.subjectIds) &&
          item.subjectIds.length > 0,
      );
    return !(hasCoreFields && hasClassSubjectCombination);
  };

  const formatSessionTime = (scheduledAt: string) =>
    new Date(scheduledAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const toDateTimeLocal = (value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const hours = String(value.getHours()).padStart(2, '0');
    const minutes = String(value.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleRescheduleRequest = (sessionId: string) => {
    const current = upcomingSessions.find((session) => session.id === sessionId);
    if (!current) return;

    const defaultDate = new Date(current.scheduledAt);
    defaultDate.setDate(defaultDate.getDate() + 1);
    setRescheduleSessionId(sessionId);
    setRescheduleReason('');
    setRescheduleDateTime(toDateTimeLocal(defaultDate));
    setRescheduleError(null);
  };

  const closeRescheduleModal = () => {
    setRescheduleSessionId(null);
    setRescheduleReason('');
    setRescheduleDateTime('');
    setRescheduleError(null);
  };

  const submitRescheduleRequest = async () => {
    if (!rescheduleSessionId) return;
    const reason = rescheduleReason.trim();
    if (!reason) {
      setRescheduleError('Please provide a reason for rescheduling.');
      return;
    }
    if (!rescheduleDateTime) {
      setRescheduleError('Please choose proposed date and time.');
      return;
    }

    const parsedProposed = new Date(rescheduleDateTime);
    if (Number.isNaN(parsedProposed.getTime())) {
      setRescheduleError('Invalid proposed date/time.');
      return;
    }
    const proposedIso = parsedProposed.toISOString();
    const currentSession = upcomingSessions.find((session) => session.id === rescheduleSessionId);
    if (!currentSession) {
      setRescheduleError('Session not found.');
      return;
    }
    try {
      await api.post('/teachers/selections/reschedule-request', {
        selectionId: currentSession.selectionId,
        sessionDate: currentSession.sessionDate,
        reason,
        proposedScheduledAt: proposedIso,
      });
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to submit reschedule request.';
      setRescheduleError(typeof message === 'string' ? message : 'Failed to submit reschedule request.');
      return;
    }

    alert('Reschedule request sent to admin for approval.');
    const currentUser = resolveUser();
    if (currentUser?.id) {
      dashboardDataService.invalidateStudentSelections();
      await loadStudentSelections(currentUser.id, true);
    }
    closeRescheduleModal();
  };

  const handleRateTeacher = async (selectionId: string) => {
    const ratingInput = window.prompt('Rate this teacher from 1 to 5');
    if (!ratingInput) return;
    const rating = Number(ratingInput);
    if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
      alert('Please enter a rating between 1 and 5.');
      return;
    }
    const review = window.prompt('Optional review') || '';

    try {
      await api.patch(`/teachers/selections/${selectionId}/rating`, {
        rating,
        review: review.trim() || undefined,
      });
      dashboardDataService.invalidateStudentSelections();
      setStudentSelections((prev) =>
        prev.map((selection) =>
          resolveSelectionId(selection) === selectionId
            ? { ...selection, studentRating: rating, studentReview: review.trim() || undefined }
            : selection,
        ),
      );
      alert('Rating submitted successfully.');
    } catch (error) {
      console.error('Error submitting teacher rating:', error);
      alert('Failed to submit rating.');
    }
  };

  const handleSetStatus = async (selectionId: string, sessionDate: string, status: 'SCHEDULED' | 'COMPLETED') => {
    try {
      await api.patch(`/teachers/selections/${selectionId}/status`, {
        sessionDate,
        status,
      });
      const currentUser = resolveUser();
      if (currentUser?.id) {
        dashboardDataService.invalidateStudentSelections();
        await loadStudentSelections(currentUser.id, true);
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message?.message ||
        error?.response?.data?.message ||
        'Failed to update session status.';
      alert(message);
    }
  };

  const recommendedTeachers = [
    { id: 1, name: 'Dr. Anderson', subject: 'Advanced Mathematics', rating: 4.9, students: 128 },
    { id: 2, name: 'Prof. Davis', subject: 'Physics II', rating: 4.8, students: 95 },
    { id: 3, name: 'Dr. Martinez', subject: 'Organic Chemistry', rating: 4.7, students: 112 },
  ];

  const quickActions = [
    { to: '/education/homework', label: 'Homework', tone: 'bg-sky-50 text-sky-800 border-sky-200 hover:bg-sky-100' },
    { to: '/student/requirements', label: 'Learning Requirements', tone: 'bg-teal-50 text-teal-800 border-teal-200 hover:bg-teal-100' },
    { to: '/education/demos', label: 'Demo Classes', tone: 'bg-lime-50 text-lime-800 border-lime-200 hover:bg-lime-100' },
    { to: '/parent/dashboard', label: 'Parent Dashboard', tone: 'bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100' },
    { to: '/student/profile', label: 'Update Profile', tone: 'bg-indigo-50 text-indigo-800 border-indigo-200 hover:bg-indigo-100' },
    { to: '/student/exams', label: 'Take Exams', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100' },
    { to: '/student/progress', label: 'Track Progress', tone: 'bg-violet-50 text-violet-800 border-violet-200 hover:bg-violet-100' },
    { to: '/student/certificates', label: 'My Certificates', tone: 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100' },
    { to: '/subscriptions', label: 'Manage Payments', tone: 'bg-cyan-50 text-cyan-800 border-cyan-200 hover:bg-cyan-100' },
  ];

  const handleReferFriend = async () => {
    try {
      setReferralLoading(true);
      const response = await api.post('/users/me/referral/generate');
      dashboardDataService.invalidateReferralSummary();
      const nextCode = String(response.data?.referralCode || '').trim();
      const nextLink = resolveReferralLink(nextCode, String(response.data?.referralLink || '').trim());
      if (!nextCode) {
        alert('Referral code could not be generated.');
        return;
      }
      setReferralCode(nextCode);
      setReferralLink(nextLink);
      const shareResult = await shareLocalAllyLink({
        title: 'Join LocalAlly',
        text: `Use my referral link to sign up on LocalAlly.`,
        url: nextLink,
      });

      if (shareResult === 'copied') {
        alert(`Referral link copied: ${nextLink}`);
      } else if (shareResult === 'unavailable') {
        alert(`Referral link: ${nextLink}`);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to generate referral code.';
      alert(message);
    } finally {
      setReferralLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[32px] border border-white/70 bg-[linear-gradient(135deg,_rgba(15,23,42,0.96)_0%,_rgba(49,46,129,0.94)_52%,_rgba(14,165,233,0.88)_100%)] px-5 py-8 text-white shadow-[0_30px_90px_rgba(15,23,42,0.16)] sm:px-8 lg:px-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
          <div>
            <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-cyan-100">
              Student Dashboard
            </span>
            <h1 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              Learn smarter, track live sessions, and keep your progress moving.
            </h1>
            <p className="mt-4 max-w-2xl text-sm text-indigo-100 sm:text-base">
              Your dashboard brings together upcoming classes, attendance checks, completed learning history, and the fastest next actions.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/student/find-teachers"
                className="inline-flex items-center justify-center rounded-2xl bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-50"
              >
                Find a Teacher
              </Link>
              <Link
                to="/student/profile"
                className="inline-flex items-center justify-center rounded-2xl border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                Update Profile
              </Link>
              <button
                type="button"
                onClick={handleReferFriend}
                disabled={referralLoading}
                className="inline-flex items-center justify-center rounded-2xl border border-emerald-300/35 bg-emerald-400/10 px-5 py-3 text-sm font-semibold text-emerald-50 transition hover:bg-emerald-400/20 disabled:opacity-60"
              >
                {referralLoading ? 'Generating...' : referralCode ? 'Copy Referral Link' : 'Refer Friend'}
              </button>
            </div>
            {referralCode && (
              <p className="mt-3 text-xs text-cyan-100">
                Referral code: {referralCode}
                {referralLink ? ` | Link ready to share` : ''}
              </p>
            )}
          </div>
          <div className="grid gap-3 rounded-[26px] border border-white/15 bg-white/10 p-4 backdrop-blur">
            <div className="rounded-2xl border border-white/10 bg-slate-950/20 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100">Today</p>
              <p className="mt-2 text-lg font-semibold">{upcomingSessions.filter((session) => isTodayKey(session.sessionDate)).length} scheduled sessions</p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100">Completion status</p>
              <p className="mt-2 text-sm text-slate-100">{completedSessions.length} sessions completed so far</p>
            </div>
          </div>
        </div>
      </section>

      {isStudentProfileIncomplete(studentProfile) && (
        <div className="rounded-[28px] border border-amber-200 bg-amber-50 px-5 py-4 shadow-sm">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-amber-800">Student profile is incomplete</p>
              <p className="text-sm text-amber-700">
                Please update date of birth, gender, location, address, and class-subject combinations to continue smoothly.
              </p>
            </div>
            <Link
              to="/student/profile"
              className="inline-flex items-center rounded-2xl bg-amber-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-amber-700"
            >
              Complete Profile
            </Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.name} className="rounded-[28px] border border-white/70 bg-white/85 p-5 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur">
            <dt className="text-sm font-medium text-slate-500 truncate">{stat.name}</dt>
            <dd className="mt-3 text-4xl font-semibold tracking-tight text-slate-900">{stat.value}</dd>
            <p className="mt-2 text-xs uppercase tracking-[0.16em] text-slate-400">Student snapshot</p>
          </div>
        ))}
      </div>

      <DemoClassPanel compact />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(300px,0.9fr)]">
        <div className="rounded-[30px] border border-white/70 bg-white/85 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Learning Queue</p>
            <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">Upcoming Sessions</h3>
            <p className="mt-1 text-sm text-slate-500">Your scheduled learning sessions and actions.</p>
          </div>
          <div className="px-5 py-4 sm:px-6">
            {upcomingSessions.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
                No upcoming sessions yet.
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingSessions.map((session) => (
                  <div key={session.id} className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4 transition hover:bg-white">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <p className="text-base font-semibold text-slate-900">
                          {resolveSubjectLabel(session.subject, session.subjectId)}
                        </p>
                        <p className="mt-1 text-sm text-slate-500">with {session.teacherName}</p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                          {session.status}
                        </span>
                        <span className="rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">
                          {formatSessionTime(session.scheduledAt)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span className="rounded-full bg-white px-3 py-1">{session.classMode}</span>
                      <span className="rounded-full bg-white px-3 py-1">{session.frequency}</span>
                      {isTodayKey(session.sessionDate) && (
                        <span className="rounded-full bg-emerald-50 px-3 py-1 font-semibold text-emerald-700">Today</span>
                      )}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        className="inline-flex items-center rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                        onClick={() => setSelectedSession(session)}
                      >
                        View Details
                      </button>
                      <button
                        className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                        onClick={() => handleRescheduleRequest(session.id)}
                      >
                        Reschedule
                      </button>
                      {session.classMode === 'ONLINE' && session.meetingLink && (
                        <a
                          href={session.meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-100"
                        >
                          Join Meeting
                        </a>
                      )}
                      {isTodayKey(session.sessionDate) && (
                        <button
                          className="inline-flex items-center rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                          onClick={() => handleSetStatus(session.selectionId, session.sessionDate, 'COMPLETED')}
                        >
                          Mark Completed
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-[30px] border border-white/70 bg-white/85 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur">
            <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Suggestions</p>
              <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">Recommended Teachers</h3>
              <p className="mt-1 text-sm text-slate-500">Based on your learning history.</p>
            </div>
            <div className="space-y-4 px-5 py-4 sm:px-6">
              {recommendedTeachers.map((teacher) => (
                <div key={teacher.id} className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-base font-semibold text-slate-900">{teacher.name}</p>
                      <p className="mt-1 text-sm text-slate-500">{teacher.subject}</p>
                    </div>
                    <div className="text-right">
                      <div className="inline-flex items-center rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                        {teacher.rating} rating
                      </div>
                      <p className="mt-2 text-xs text-slate-500">{teacher.students} students</p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                    >
                      View Profile
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Message
                    </button>
                  </div>
                </div>
              ))}
              <Link to="/student/find-teachers" className="inline-flex items-center text-sm font-semibold text-cyan-700 hover:text-cyan-800">
                View all teachers<span aria-hidden="true" className="ml-1">&rarr;</span>
              </Link>
            </div>
          </div>

          <div className="rounded-[30px] border border-white/70 bg-white/85 p-5 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur sm:p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Quick Actions</p>
            <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">Move faster</h3>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {quickActions.map((action) => (
                <Link
                  key={action.to}
                  to={action.to}
                  className={`inline-flex items-center justify-center rounded-2xl border px-4 py-3 text-sm font-semibold transition ${action.tone}`}
                >
                  {action.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <section className="rounded-[30px] border border-white/70 bg-white/85 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur">
        <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">History</p>
          <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">Completed Sessions</h3>
          <p className="mt-1 text-sm text-slate-500">Daily status history. Update if yesterday was missed.</p>
        </div>
        <div className="px-5 py-4 sm:px-6">
          {completedSessions.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
              No completed sessions yet.
            </div>
          ) : (
            <div className="space-y-4">
              {completedSessions.map((session) => (
                <div key={session.id} className="rounded-[24px] border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-base font-semibold text-slate-900">{resolveSubjectLabel(session.subject, session.subjectId)}</p>
                      <p className="mt-1 text-sm text-slate-500">with {session.teacherName}</p>
                      <p className="mt-2 text-xs text-slate-500">{formatSessionTime(session.scheduledAt)}</p>
                    </div>
                    <div className="sm:text-right">
                      <p className={`text-sm font-semibold ${session.status === 'COMPLETED' ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {session.status}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2 sm:justify-end">
                        {session.status === 'COMPLETED' ? (
                          <button
                            type="button"
                            onClick={() => handleSetStatus(session.selectionId, session.sessionDate, 'SCHEDULED')}
                            className="inline-flex items-center rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 transition hover:bg-amber-100"
                          >
                            Mark Pending
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleSetStatus(session.selectionId, session.sessionDate, 'COMPLETED')}
                            className="inline-flex items-center rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                          >
                            Mark Completed
                          </button>
                        )}
                        {session.status === 'COMPLETED' && (
                          session.rating ? (
                            <span className="inline-flex items-center rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
                              Rated: {session.rating}/5
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleRateTeacher(session.selectionId)}
                              className="inline-flex items-center rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                            >
                              Rate Teacher
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {rescheduleSessionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md space-y-4 rounded-[28px] border border-white/70 bg-white/95 p-6 shadow-[0_30px_80px_rgba(15,23,42,0.18)] backdrop-blur">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-base font-semibold text-slate-900">Reschedule Session</p>
                <p className="mt-1 text-xs text-slate-600">Choose a new date/time and add the reason.</p>
              </div>
              <button
                className="text-sm text-slate-500 hover:text-slate-700"
                onClick={closeRescheduleModal}
              >
                Close
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Reason</label>
                <textarea
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  rows={3}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Reason for rescheduling"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Proposed Date & Time</label>
                <input
                  type="datetime-local"
                  value={rescheduleDateTime}
                  onChange={(e) => setRescheduleDateTime(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {rescheduleError && <p className="text-xs text-red-700">{rescheduleError}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeRescheduleModal}
                className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitRescheduleRequest}
                className="rounded-2xl bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md space-y-3 rounded-[28px] border border-white/70 bg-white/95 p-6 shadow-[0_30px_80px_rgba(15,23,42,0.18)] backdrop-blur">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">Session Details</p>
                <p className="text-xs text-slate-600">
                  {resolveSubjectLabel(selectedSession.subject, selectedSession.subjectId)} · {selectedSession.classMode} · {selectedSession.frequency}
                </p>
              </div>
              <button
                className="text-sm text-slate-500 hover:text-slate-700"
                onClick={() => setSelectedSession(null)}
              >
                Close
              </button>
            </div>
            <div className="space-y-2 text-sm text-slate-700">
              <div className="flex justify-between">
                <span className="text-slate-500">Teacher</span>
                <span className="font-medium">{selectedSession.teacherName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Scheduled</span>
                <span className="font-medium">{formatSessionTime(selectedSession.scheduledAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Status</span>
                <span className="font-medium">{selectedSession.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Duration</span>
                <span className="font-medium">{selectedSession.duration} min</span>
              </div>
              {selectedSession.classMode === 'ONLINE' && selectedSession.meetingLink && (
                <a
                  href={selectedSession.meetingLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-700 hover:text-cyan-800"
                >
                  Join Meeting
                </a>
              )}
              {selectedSession.classMode === 'OFFLINE' && (
                <div className="pt-2 space-y-2">
                  <button
                    type="button"
                    onClick={handleStartScanner}
                    className="w-full rounded-2xl bg-slate-100 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-200"
                  >
                    Scan QR with Camera
                  </button>
                  <div className="space-y-1">
                    <label className="text-xs text-slate-600">Scan result / Meeting ID</label>
                    <input
                      type="text"
                      value={attendanceInput}
                      onChange={(e) => setAttendanceInput(e.target.value)}
                      placeholder="Paste scanned QR text or enter meeting ID"
                      className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleVerifyAttendance}
                    className="w-full rounded-2xl bg-slate-950 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    Verify & Record Attendance
                  </button>
                  {attendanceStatus && (
                    <p
                      className={`text-xs font-medium ${
                        attendanceStatus.ok ? 'text-green-700' : 'text-red-700'
                      }`}
                    >
                      {attendanceStatus.message}
                    </p>
                  )}
                  {scannerError && <p className="text-xs text-red-700">{scannerError}</p>}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showScanner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md space-y-3 rounded-[28px] border border-white/70 bg-white/95 p-4 shadow-[0_30px_80px_rgba(15,23,42,0.18)] backdrop-blur">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-900">Scan QR Code</p>
              <button
                className="text-sm text-slate-500 hover:text-slate-700"
                onClick={() => {
                  setShowScanner(false);
                  if (streamRef.current) {
                    streamRef.current.getTracks().forEach((t: MediaStreamTrack) => t.stop());
                    streamRef.current = null;
                  }
                }}
              >
                Close
              </button>
            </div>
            <video ref={videoRef} className="aspect-video w-full rounded-2xl bg-black" />
            <p className="text-center text-xs text-slate-500">Align the QR inside the frame.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
